import { useQueries } from '@tanstack/react-query'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api, query } from '../../api/client'
import type { AppSummary, RatingRow } from '../../api/types'
import { formatDate } from '../../lib/format'
import { SERIES_COLORS } from '../../lib/rank-series'

/** Average rating per app over time in one storefront. */
export function CompareRatings({ apps, storefront, from }: { apps: AppSummary[]; storefront: string; from: string }) {
	const results = useQueries({
		queries: apps.map((a) => ({
			queryKey: ['ratings', a.id, storefront, from],
			queryFn: () => api.get<RatingRow[]>(`/api/v1/apps/${a.id}/ratings${query({ storefront, from })}`),
		})),
	})
	const byDate = new Map<string, Record<string, number | string | null>>()
	results.forEach((r, i) => {
		for (const row of r.data ?? []) {
			const entry = byDate.get(row.date) ?? { date: row.date }
			entry[`a${apps[i].id}`] = row.ratingAvg
			byDate.set(row.date, entry)
		}
	})
	const data = [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)))
	if (data.length < 2) return <div className="empty">Not enough daily ratings in {storefront.toUpperCase()} yet.</div>
	return (
		<ResponsiveContainer width="100%" height={320}>
			<LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
				<CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
				<XAxis dataKey="date" tickFormatter={(d) => formatDate(d)} stroke="var(--text-3)" fontSize={12} />
				<YAxis domain={['auto', 'auto']} stroke="var(--text-3)" fontSize={12} width={40} />
				<Tooltip
					labelFormatter={(d) => formatDate(String(d))}
					contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
				/>
				{apps.map((a, i) => (
					<Line
						key={a.id}
						dataKey={`a${a.id}`}
						name={a.name}
						stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
						strokeWidth={2}
						dot={false}
						connectNulls
						isAnimationActive={false}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	)
}
